import * as React from "react";
import styled, { StyledComponent } from "styled-components";
import scrollParent from "utils/scrollParent";
import throttle from "utils/throttle";
import { abortedPromise, IAbortedPromise, ABORT_REASON } from "utils/abortedPromise";

export const HoldContainer = styled.section``;
export interface IScrollLoadFunctionReturnType {
	loadCount: number;
	isError: boolean;
}

export interface IScrollLoadProps {
	StyleComponent?: StyledComponent<"section", any>;
	loadFunction: () => Promise<IScrollLoadFunctionReturnType>;
	// limit: number;
	isDisable?: boolean;
	dataKey?: string;
	loadDataWhenDidMount?: boolean;
	needRetryIfError?: boolean;
	isShowLoading?: boolean;
	orientation?: "horizontal" | "vertical";
	className?: string;
	style?: React.CSSProperties;
	grid?: {
		gridCol?: number;
		gridGap?: number;
		parentGap?: number;
	};
}

export enum EDataLoadStatus {
	ALREADY_LOAD_ALL_DATA,
	LOADING_DATA,
	LOAD_DATA_FAIL,
	WAIT_FOR_TRIGGER_LOAD,
}

interface ScrollLoadState {
	dataLoadStatus: EDataLoadStatus;
	prevDataKey?: string;
}

export default class ScrollLoad extends React.Component<IScrollLoadProps, ScrollLoadState> {
	static defaultProps = {
		loadDataWhenDidMount: true,
		needRetryIfError: false,
		isShowLoading: false,
	};
	state: ScrollLoadState = {
		dataLoadStatus: EDataLoadStatus.WAIT_FOR_TRIGGER_LOAD,
		prevDataKey: this.props.dataKey || "",
	};
	scrollContainer: Node;
	scrollContainerElement: HTMLElement;
	stuffsContainer: HTMLDivElement;
	onScrollLoad: () => any;
	cancelPrevLoadPromise: IAbortedPromise<IScrollLoadFunctionReturnType>;

	constructor(props: IScrollLoadProps) {
		super(props);
		this.onScrollLoad = throttle(this.scrollLoad, 120);
	}

	static getDerivedStateFromProps(nextProps: IScrollLoadProps, prevState: ScrollLoadState) {
		if (prevState.prevDataKey && nextProps.dataKey !== prevState.prevDataKey) {
			return {
				prevDataKey: nextProps.dataKey,
				dataLoadStatus: EDataLoadStatus.WAIT_FOR_TRIGGER_LOAD,
			};
		}
		return prevState;
	}

	componentDidMount() {
		this.scrollContainerElement = scrollParent(this.stuffsContainer);
		// this.scrollContainer = (!this.scrollContainerElement.parentElement
		// 	? document
		// 	: this.scrollContainerElement) as Node;
		this.scrollContainer = document;
		this.scrollContainer.addEventListener("scroll", this.onScrollLoad);

		if (this.props.loadDataWhenDidMount) {
			this.loadData();
		}
	}

	componentDidUpdate(...updateProps: [IScrollLoadProps, ScrollLoadState]) {
		const [, prevState] = updateProps;
		if (prevState.prevDataKey && this.state.prevDataKey !== prevState.prevDataKey) {
			if (this.cancelPrevLoadPromise) {
				this.cancelPrevLoadPromise.abort();
				this.cancelPrevLoadPromise = void 0 as any;
			}
			if (this.props.loadDataWhenDidMount) {
				this.loadData();
			}
		}
	}

	componentWillUnmount() {
		if (this.scrollContainer) {
			this.scrollContainer.removeEventListener("scroll", this.onScrollLoad);
		}
		if (this.cancelPrevLoadPromise) {
			this.cancelPrevLoadPromise.abort();
		}
	}

	initStatus = (status: EDataLoadStatus) => {
		this.setState({ dataLoadStatus: status });
	};

	scrollLoad = () => {
		if (this.stuffsContainer) {
			const parentHeight = window.screen.availHeight;
			const innerRect = this.stuffsContainer.getBoundingClientRect();
			const { orientation } = this.props;
			const isNeedLoad =
				(orientation !== "horizontal" && innerRect.bottom < parentHeight * 4) ||
				(orientation === "horizontal" &&
					this.scrollContainerElement.scrollWidth -
						this.scrollContainerElement.scrollLeft <
						this.scrollContainerElement.offsetWidth * 2);
			if (isNeedLoad && this.state.dataLoadStatus === EDataLoadStatus.WAIT_FOR_TRIGGER_LOAD) {
				this.loadData();
			}
		}
	};

	processLoadError = (error?: any) => {
		if (error !== ABORT_REASON) {
			if (this.props.needRetryIfError) {
				this.setState({ dataLoadStatus: EDataLoadStatus.LOAD_DATA_FAIL });
			} else {
				this.setState({ dataLoadStatus: EDataLoadStatus.WAIT_FOR_TRIGGER_LOAD });
			}
		}
		this.cancelPrevLoadPromise = void 0 as any;
	};

	loadData() {
		const { loadFunction, isDisable } = this.props;
		if (isDisable) {
			return;
		}
		if (this.state.dataLoadStatus === EDataLoadStatus.LOADING_DATA) {
			return;
		}

		this.setState({ dataLoadStatus: EDataLoadStatus.LOADING_DATA });

		this.cancelPrevLoadPromise = abortedPromise(loadFunction());
		this.cancelPrevLoadPromise.then(({ loadCount, isError }) => {
			if (loadCount !== -999) {
				if (isError) {
					this.processLoadError();
				} else {
					this.setState({ dataLoadStatus: EDataLoadStatus.WAIT_FOR_TRIGGER_LOAD });
					this.cancelPrevLoadPromise = void 0 as any;
				}
			}
		}, this.processLoadError);
	}

	render() {
		const { StyleComponent = HoldContainer, grid = {}, style = {} } = this.props;
		return (
			<StyleComponent
				ref={(me: HTMLDivElement) => (this.stuffsContainer = me)}
				className={this.props.className}
				{...grid}
				style={style}
				data-panel={"scrollpanel"}>
				{this.props.children}
			</StyleComponent>
		);
	}
}
